<script setup lang="ts">
import BaseAlert from "@/components/Base/Alert.vue";
import BaseHeadline from "@/components/Base/Headline.vue";
import IconButton from "@/components/IconButton.vue";
import ListSchoolClassItem from "@/components/ListSchoolClassItem.vue";
import HomeModalVideo from "@/components/home/ModalVideo.vue";
import HomeMoreLink from "@/components/home/MoreLink.vue";
import notebook from "@/images/undraw_notebook_re_id0r-2.svg";
import type { SchoolClass } from "@/services/fobizzToolsApi";

type Props = { schoolClasses?: SchoolClass[] };

defineProps<Props>();

const { browserLanguage } = useBrowserLanguage();
const { t } = useI18n();

const isGermanBrowserLanguage = computed(() => {
  return browserLanguage.value === "de";
});

const modalIsOpen = ref(false);
const openModal = () => {
  modalIsOpen.value = true;
};

const handleModalHide = () => {
  modalIsOpen.value = false;
};
</script>

<template>
  <div class="d-flex flex-column">
    <BaseHeadline tag="h3" weight="medium" class="mb-2">
      {{ t("startPage.headers.mySchoolClasses") }}
    </BaseHeadline>
    <ul
      v-if="schoolClasses && schoolClasses?.length > 0"
      id="items--sharedSpaces"
      class="basic-list basic-list--border mb-2"
    >
      <ListSchoolClassItem
        v-for="schoolClass in schoolClasses"
        :key="schoolClass.id"
        :item="schoolClass"
      />
    </ul>
    <div v-else class="h-100">
      <BaseAlert
        :title="t('startPage.headers.mySchoolClasses')"
        class="mb-2"
        state="light"
      >
        <div>
          {{ t("startPage.paragraphs.mySchoolClasses") }}
        </div>
        <template #actions>
          <div class="d-flex flex-column gap-2 flex-xl-row">
            <IconButton
              icon-name="plus-lg"
              icon-color="light"
              kind="primary"
              size="sm"
              rounded
              icon-position="left"
              no-wrap
              :label="t('startPage.links.createClass')"
              class="mt-auto"
              type="button"
              :to="{
                name: 'teacher-school-class-new',
              }"
            />

            <IconButton
              v-if="isGermanBrowserLanguage"
              kind="link"
              type="button"
              size="sm"
              inline
              weight="semibold"
              icon-position="left"
              icon-name="play-btn"
              icon-size="md"
              @click="openModal"
            >
              {{ t("startPage.links.watchTutorial") }}
            </IconButton>
          </div>
        </template>
        <template #second-column>
          <img
            :src="notebook"
            style="max-width: 200px; max-height: 125px"
            class="d-none d-lg-block"
            alt="Notebook"
            width="100"
            height="auto"
          />
        </template>
      </BaseAlert>
    </div>
    <HomeMoreLink
      data-testid="link-to-my-school-classes"
      to="/t/school_classes"
      :title="t('startPage.links.mySchoolClasses')"
    />
  </div>

  <HomeModalVideo
    v-if="modalIsOpen"
    :open="modalIsOpen"
    :video-id="t('startPage.schoolClasses.videoId')"
    :video-title="t('startPage.schoolClasses.videoTitle')"
    @hide="handleModalHide"
  />
</template>

<style lang="scss" scoped>
.basic-list {
  list-style-type: none;
  padding: 0;
}
</style>
